import {
  Box,
  CheckboxField,
  DateField,
  Label,
  NumberField,
  SingleSelectField,
  TextField,
  ToggleButtonGroupField,
  HStack,
  Heading,
} from '@medely/ui-kit';
import React, { useEffect } from 'react';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  LOCATION_IS_TEACHING_HOSPITAL_OPTIONS,
  NON_CLINICAL_EMPLOYMENT_TYPE_OPTIONS,
  TRAUMA_LEVEL_OPTIONS,
  YES_NO_OPTIONS,
} from '../constants';
import { ContentLoader } from '@mp/components/ContentLoader';
import { useAllPositionsForWorkHistoryModal, useLocationTypes, useStates } from '../graphql';
import { useWatch, useFormContext } from '@medely/ui-kit/react-hook-form';

type WorkHistoryFormProps = {
  isClinical: boolean;
  isEdit: boolean;
};

export const WorkHistoryForm = ({ isClinical, isEdit }: WorkHistoryFormProps) => {
  const isWeb = typeof document !== 'undefined';
  const { setValue, clearErrors } = useFormContext();

  const { allPositions, isLoading: isLoadingAllPositions } = useAllPositionsForWorkHistoryModal(
    true,
    isClinical,
  );
  const positionOptions = isClinical
    ? allPositions?.map(({ id, display_name }) => ({
        value: id as number,
        label: display_name as string,
      }))
    : undefined;

  const { locationTypes, isLoading: isLoadingLocationTypes } = useLocationTypes(isClinical);
  const locationTypeOptions = isClinical
    ? locationTypes?.map(({ id, label }) => ({
        value: id as number,
        label: label as string,
      }))
    : undefined;

  const { states, isLoading: isLoadingStates } = useStates();
  const statesOptions = states.map(({ abbreviation, id }) => ({
    value: id as number,
    label: abbreviation as string,
  }));

  const isLoading =
    isLoadingStates || (isClinical && (isLoadingAllPositions || isLoadingLocationTypes));

  const employmentTypeOptions = (
    isClinical ? EMPLOYMENT_TYPE_OPTIONS : NON_CLINICAL_EMPLOYMENT_TYPE_OPTIONS
  ).map((option) => ({ ...option, value: String(option.value) }));

  const currentlyWorkHere = useWatch({ name: 'currently_work_here' });
  const displaysFloatingDetails = useWatch({ name: 'floating' });
  const selectedLocationTypeId = useWatch({ name: 'location_type_id' });
  const isHospital = selectedLocationTypeId === 8;
  const endDate = useWatch({ name: 'end_date' });

  useEffect(() => {
    if (currentlyWorkHere) {
      setValue('end_date', null);
      clearErrors('end_date');
    } else {
      setValue('end_date', endDate);
      clearErrors('end_date');
    }
  }, [currentlyWorkHere, endDate]);

  useEffect(() => {
    if (!isHospital) {
      setValue('location_trauma_level', null);
      setValue('location_number_of_beds', null);
      setValue('location_is_teaching', null);
      clearErrors('location_trauma_level');
      clearErrors('location_number_of_beds');
      clearErrors('location_is_teaching');
    }
  }, [isHospital, setValue, clearErrors]);

  return (
    <ContentLoader loading={isLoading}>
      <>
        {!isWeb && (
          <Box py={1}>
            <Heading size="xs">
              {isClinical
                ? `${isEdit ? 'Edit' : 'Add'} clinical experience`
                : `${isEdit ? 'Edit' : 'Add'} non-clinical experience`}
            </Heading>
          </Box>
        )}
        {positionOptions && isClinical && (
          <>
            <SingleSelectField label="Position *" name="position_id" options={positionOptions} />
          </>
        )}
        <Box py={1} />
        <TextField name="position_title" label={isClinical ? 'Job title' : 'Job title *'} />
        <Box py={1} />
        {isClinical && (
          <>
            <TextField name="unit" label="Unit *" />
            <Box py={1} />
            <ToggleButtonGroupField
              label="Did you float to other units?"
              name="floating"
              options={YES_NO_OPTIONS}
            />
            {displaysFloatingDetails && (
              <>
                <Box py={1} />
                <TextField
                  name="floating_details"
                  rows={4}
                  multiline
                  label="Units and hrs/week floated"
                  helperText="List the hours per week you worked in each unit"
                />
              </>
            )}
          </>
        )}
        <CheckboxField label="I’m currently working in this position" name="currently_work_here" />
        <Box py={1} />
        <HStack gap={2}>
          <Box flex={1}>
            <DateField
              name="start_date"
              label="Start date *"
              parseValue={(value) => (value ? value : null)}
            />
          </Box>
          <Box flex={1}>
            <DateField
              name="end_date"
              label="End date *"
              disabled={!!currentlyWorkHere}
              parseValue={(value) => (value ? value : null)}
            />
          </Box>
        </HStack>
        <Box py={1} />
        <HStack justifyContent="space-between">
          <Box width={isClinical ? '60%' : '100%'}>
            <SingleSelectField
              label="Employment type *"
              name="employment_type"
              options={employmentTypeOptions}
              parseValue={(value) =>
                value === undefined || value === null ? value : String(value)
              }
              formatOnChange={(value) => (value === '' ? undefined : Number(value))}
            />
          </Box>
          {isClinical && (
            <Box width="36%">
              <NumberField name="hours_per_week" label="Hrs/week" />
            </Box>
          )}
        </HStack>
        <Box py={1} />
        <TextField name="description" rows={4} multiline label="Description of job duties *" />
        <Box py={1}>
          <Label size="l">{isClinical ? 'Facility details' : 'Company details'}</Label>
        </Box>
        <TextField
          name="organization_name"
          label={isClinical ? 'Facility name *' : 'Company name *'}
        />
        {isClinical && (
          <>
            <Box py={1} />
            <SingleSelectField
              label="Facility type *"
              name="location_type_id"
              options={locationTypeOptions || []}
            />
            {isHospital && (
              <>
                <Box py={1} />
                <HStack justifyContent="space-between">
                  <Box width="58%">
                    <SingleSelectField
                      label="Trauma level"
                      name="location_trauma_level"
                      options={TRAUMA_LEVEL_OPTIONS}
                    />
                  </Box>
                  <Box width="38%">
                    <NumberField name="location_number_of_beds" label="# of beds" />
                  </Box>
                </HStack>
                <Box py={1} />
                <ToggleButtonGroupField
                  label="Is the facility a teaching hospital?"
                  name="location_is_teaching"
                  options={LOCATION_IS_TEACHING_HOSPITAL_OPTIONS}
                />
              </>
            )}
          </>
        )}
        <Box py={1} />
        <HStack justifyContent="space-between" mb={isWeb ? 6 : 2}>
          <Box width="60%">
            <TextField name="location" label="City *" />
          </Box>
          <Box width="36%">
            <SingleSelectField label="State *" name="state_id" options={statesOptions} />
          </Box>
        </HStack>
        <Box py={1} />
      </>
    </ContentLoader>
  );
};
